import Tools from '../Tools/Tools';

import LineGraphConfig from './LineGraphConfig';
/**
 * Třída pro vytváření spojnicového grafu.
 */
class LineGraph {
	constructor(ctx, data, section) {
		this.ctx = ctx;
		this.data = data;
		this.section = section;
		this.graphOptions = null;

		this.setGraphConfig();

		// Barvy pro CPT, CTR apod.
		this.colors = {
			clicks: '#FFBC2F',
			commission: '#69B2E8',
			cpt: '#787878',
			ctr: '#3AC9A1',
			impressions: '#D2001A',
		};

		this.createGraph();
	}

	/**
	 * Nastaví potřebné parametry a callbacky.
	 */
	setGraphConfig() {
		this.graphOptions = LineGraphConfig;

		// Vypočítá popisky na ose Y včetně korektních labelu (cpt, ctr apod.).
		this.graphOptions.scales.yAxes[0].ticks.callback = (label) => this.getLabel(label);

		// Nastavuje data pro tooltip.
		this.graphOptions.tooltips.callbacks.label = (tooltipItem, data) => {
			const { title } = data.datasets[tooltipItem.datasetIndex];

			const value = Tools.formatValue(
				data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index],
				data.datasets[tooltipItem.datasetIndex].label,
			);

			return value + '~' + title; // Podle ~ je v custom tooltipu rozdělován string.
		};
	}

	/**
	 * Přidá metriku do grafu.
	 * @param {string} metric Typ metriky.
	 * @param {string} title Přeložený typ metriky.
	 */
	addMetric(metric, title) {
		this.graph.data.datasets.push(this.getDataset(metric, title));
		this.graph.update();
	}

	/**
	 * Odstraní metriku z grafu.
	 * @param {string} metric
	 */
	removeMetric(metric) {
		let index = null;

		// Metrika je nalezena v datasetu a odstraněna.
		this.graph.data.datasets.forEach((dataset, i) => {
			if (dataset.label === metric) {
				index = i;
			}
		});

		this.graph.data.datasets.splice(index, 1);
		this.graph.update();
	}

	/**
	 * Pokud je vybraných více metrik, tak se nezobrazují popisky,
	 * ale pouze naformátované hodnoty.
	 * Pouze u CPT a commission se zobrazí hodnota, protože
	 * jsou obě metriky v Kč.
	 */
	getLabel(label) {
		if (
			this.selected.length === 1 ||
			(this.selected.length === 2 && this.selected.includes('cpt') && this.selected.includes('commission'))
		) {
			return Tools.formatValue(label, this.selected[0]);
		} else {
			return Tools.formatValue(label, 'number');
		}
	}

	/**
	 * Labels - popisky na ose Y.
	 * ! jejich počet musí odpovídat počtům hodnot, jinak knihovna selže.
	 * @return {[]} Pole labelů.
	 */
	getLabels() {
		const data = this.data.series[Object.keys(this.data.series)[0]].map((value) => value.date);

		// Chart.js nezobrazuje data (čáry), pokud nejsou aspon dve hodnoty.
		if (data.length === 1) {
			return [...data, ...data];
		} else {
			return data;
		}
	}

	/**
	 * Nastaví nová data a překreslí graf.
	 * @param {object} data data.series data.header
	 */
	updateDataset(data) {
		this.data = data;
		this.updateHeader();
		this.graph.data.labels = this.getLabels();
		this.graph.data.datasets = this.getDatasets();
		this.graph.update();
	}

	/**
	 * Jeden dataset představuje například CTR
	 * @param {string} property
	 * @return {object}
	 */
	getDataset(property, title = '') {
		const line = {
			backgroundColor: 'rgba(100, 0, 0, 0)',
			borderColor: this.colors[property],
			label: property,
			lineTension: 0,
			pointHoverBackgroundColor: this.colors[property],
			pointHoverRadius: 5,
			radius: 0,
			title,
		};

		// z API dostáváme hodnoty ve tvaru date, value (chceme pouze value)
		const data = this.data.series[property].map((value) => value.value);

		// Chart.js nezobrazuje data (čáry), pokud nejsou aspon dve hodnoty.
		if (data.length === 1) {
			line.data = [...data, ...data];
		} else {
			line.data = data;
		}

		return line;
	}

	/**
	 * Naformátuje hodnoty v grafu.
	 * @return {[]} Vrací pole objektů, kde každý objekt představu CPT, CTR apod.
	 */
	getDatasets() {
		const dataset = [];

		this.selected.forEach((property) => {
			const data = this.data.header.filter((metric) => metric.field === property);

			if (data.length) {
				const [{ title }] = data;

				dataset.push(this.getDataset(property, title));
			}
		});

		return dataset;
	}

	/**
	 * Naformátuje data pro graf.
	 * @return {object}
	 */
	formatData() {
		const data = {
			bezierCurve: false,
			datasets: this.getDatasets(),
			labels: this.getLabels(),
		};

		return data;
	}

	/**
	 * Kliknutí na metriku - CPT, CTR apod.
	 * Do grafu bude přidána/odebrána.
	 * @param {*} e
	 */
	selectMetric(e) {
		let elem = null;
		let metric = null;
		let title = null;

		// Uživatel může kliknout na wrapper nebo vnitřní hodnotu (title/value)
		if (e.target.getAttribute('data-type')) {
			metric = e.target.getAttribute('data-type');
			title = e.target.querySelector('.Graph-button-title').innerText;
			elem = e.target;
		} else if (e.target.parentElement.getAttribute('data-type')) {
			metric = e.target.parentElement.getAttribute('data-type');

			title = e.target.parentElement.querySelector('.Graph-button-title').innerText;

			elem = e.target.parentElement;
		} else {
			// Klik na tooltip
			return;
		}

		if (elem.classList.toggle('is-active')) {
			this.selected.push(metric);
			this.addMetric(metric, title);
		} else {
			this.selected.splice(this.selected.indexOf(metric), 1);
			this.removeMetric(metric);
		}

		Tools.updateLocalStorage('graphSelected', this.selected);
	}

	/**
	 * Obnoví hodnoty v headeru grafu.
	 */
	updateHeader() {
		this.graphControls.innerHTML = '';
		this.renderHeaderMetrics();
	}

	/**
	 * Vygeneruje hlavičku grafu
	 * Zobrazení - Kliky apod.
	 */
	renderHeader() {
		this.graphControls = Tools.createElement('div', {
			class: 'Graph-controls',
		});

		this.renderHeaderMetrics();

		// Zkontroluje jestli v section už něco je (koláčový graf tam může být).
		// Pokud ano, tak header vloží před to, protože musí být na začátku.
		if (this.section.firstChild) {
			this.section.insertBefore(this.graphControls, this.section.firstChild);
		} else {
			this.section.appendChild(this.graphControls);
		}
	}

	/**
	 * Vyrenderuje jednotlivé taby (metriky) pro čárový graf.
	 */
	renderHeaderMetrics() {
		this.data.header.forEach((head) => {
			const metric = Tools.createElement('div', {
				class: 'Graph-button Graph-button--' + head.field,
				'data-type': head.field,
			});

			// pokud je metrika v poli vybraných, tak jí dej active
			if (this.selected.indexOf(head.field) >= 0) {
				metric.classList.add('is-active');
			}

			metric.addEventListener('click', this.selectMetric.bind(this));

			const title = Tools.createElement('span', {
				class: 'Graph-button-title',
				innerHTML: head.title,
			});

			const value = Tools.createElement('span', {
				class: 'Graph-button-value',
				innerHTML: Tools.formatValue(head.value, head.type),
			});

			Tools.appendChilds(metric, title, value);

			if (head.tooltip) {
				const tooltip = Tools.createTooltip(head.tooltip);

				tooltip.classList.add('Tooltip--graph');
				metric.appendChild(tooltip);
			}

			this.graphControls.appendChild(metric);
		});
	}

	/**
	 * Vytvoří nový graf.
	 */
	createGraph() {
		this.selected = Tools.getFromLocalStorage('graphSelected', ['impressions', 'cpt', 'commission']);

		this.renderHeader();
		this.graph = new Chart(this.ctx, {
			data: this.formatData(),
			options: this.graphOptions,
			type: 'line',
		});

		this.graph.options.tooltips.custom = this.tooltipCreator.bind(this);
		this.tooltip = this.generateTooltip();
	}

	/**
	 * Metoda, která obsluhuje tooltip grafu
	 * @param {object} tooltipModel - objekt, který obsahuje vše ohledně tooltipu
	 */
	tooltipCreator(tooltipModel) {
		if (tooltipModel.opacity === 0) {
			this.hideTooltip(this.tooltip);

			return;
		}

		// Přegenerování nového textu
		if (tooltipModel.body) {
			this.regenerateTooltipContent(this.tooltip, tooltipModel);
		}

		this.tooltip.style.zIndex = 1;
		this.tooltip.style.opacity = 1;

		this.tooltip.style.top = this.graph.canvas.offsetTop + this.graph.canvas.offsetHeight - tooltipModel.height + 'px';

		if (tooltipModel.caretX < this.graph.width / 2) {
			// 40 is fuckin magic number...
			this.tooltip.classList.add('Graph-tooltip--right');
			this.tooltip.classList.remove('Graph-tooltip--left');

			this.tooltip.style.left = this.graph.canvas.offsetLeft + tooltipModel.caretX + 40 + 'px';
		} else {
			const tooltipWidth = 255;

			this.tooltip.classList.add('Graph-tooltip--left');
			this.tooltip.classList.remove('Graph-tooltip--right');

			this.tooltip.style.left = this.graph.canvas.offsetLeft + tooltipModel.caretX - tooltipWidth + 'px';
		}
	}

	/**
	 * Metoda, která schová tooltip pokud není graf aktivní
	 * @param {object} tooltip - objekt, který obsahuje HTML tooltip
	 */
	hideTooltip(tooltip) {
		tooltip.style.zIndex = 0;
		tooltip.style.opacity = 0;
	}

	/**
	 * Metoda, která vygeneruje tooltip
	 */
	generateTooltip() {
		const tooltip = Tools.createElement('div', {
			class: 'Graph-tooltip',
			id: 'Graph-tooltip',
			style: 'position: absolute; pointer-events: none; left: -5000px; top: -5000px',
		});

		document.body.appendChild(tooltip);

		return tooltip;
	}

	/**
	 * Metoda, přeregeneruje tooltip (jeho hodnoty atd.)
	 * @param {object} tooltip - objekt, který obsahuje HTML tooltip
	 * @param {object} tooltipModel - objekt, který obsahuje vše ohledně tooltipu
	 */
	regenerateTooltipContent(tooltip, tooltipModel) {
		const titleText = tooltipModel.title[0] || '';

		const title = Tools.createElement('h6', {
			class: 'Graph-tooltip-title',
			innerText: titleText,
		});

		const list = Tools.createElement('ul', { class: 'Graph-tooltip-list' });

		const bodyLines = tooltipModel.body.map((bodyItem) => bodyItem.lines[0]);

		// Vymaže se vše co je v tooltipu, aby se to mohlo znova nastavit.
		this.tooltip.innerHTML = '';

		bodyLines.forEach((content, i) => {
			const colors = tooltipModel.labelColors[i];

			const colorCube = Tools.createElement('span', {
				class: 'Graph-tooltip-cube',
				style: `background: ${colors.borderColor};`,
			});
			const metricValue = Tools.createElement('span', {
				class: 'Graph-tooltip-item-value',
				innerText: `${content.split('~')[0]}`,
			});
			const metricTitle = Tools.createElement('span', {
				class: 'Graph-tooltip-item-title',
				innerText: `${content.split('~')[1]}`,
			});

			const li = Tools.createElement('li', { class: 'Graph-tooltip-item' });

			Tools.appendChilds(li, colorCube, metricValue, metricTitle);
			list.appendChild(li);
		});

		Tools.appendChilds(this.tooltip, title, list);
	}
}

export default LineGraph;
